import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Download, FileCode, TerminalSquare, Box, ExternalLink, Copy, Check, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { selectSettings } from '../store/slices/settingsSlice';
import { getAuthRedirectPath } from '../utils/authRedirect';

const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error('Copy command failed');
  }
};

const DownloadCard = ({ icon: Icon, title, description, link, type, t }) => {
  const hasLink = Boolean(link && String(link).trim());
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const normalizedType = typeof type === 'string' ? type.trim().toLowerCase() : '';

  const handleCopyLink = async () => {
    if (!hasLink || copying) return;

    setCopying(true);
    try {
      await copyToClipboard(link);
      setCopied(true);
      toast.success(t('downloads.link_copied', 'Link copied'));
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t('downloads.copy_failed', 'Failed to copy link'));
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="group overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-500/40">
      <div className="flex items-start gap-3 p-3 sm:p-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100 dark:bg-white dark:text-gray-950 dark:ring-0">
          <Icon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-black leading-tight text-gray-950 dark:text-white notranslate" translate="no">
                {title}
              </h3>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                {description}
              </p>
            </div>
            {normalizedType && (
              <span className="shrink-0 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[9px] font-black uppercase text-primary-700 dark:border-primary-500/30 dark:bg-primary-500/10 dark:text-primary-300">
                {normalizedType === 'lua' ? 'Lua' : 'Json'}
              </span>
            )}
          </div>

          {(normalizedType === 'lua' || normalizedType === 'json') && (
            <p className="mt-2 line-clamp-1 text-[11px] font-bold text-blue-700 dark:text-blue-300">
              {normalizedType === 'lua'
                ? t('downloads.gg_hint', 'Download GG files if you want to use Game Guardian.')
                : t('downloads.mod_hint', 'Download Mod Menu files if you want to use RuntimeX / Js Hook.')}
            </p>
          )}

          <p className="mt-2 truncate rounded-xl border border-gray-100 bg-gray-50 px-2 py-1.5 font-mono text-[11px] text-gray-500 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-400">
            {hasLink ? link : t('downloads.link_not_configured', 'Link not configured yet')}
          </p>

          {hasLink ? (
            <div className="mt-3 flex items-center gap-2">
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-9 flex-1 items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 text-xs font-black text-white transition hover:bg-primary-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
              >
                <Download className="w-4 h-4" />
                <span>{t('downloads.download_button', 'Download')}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={handleCopyLink}
                disabled={copying}
                className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-black text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? t('downloads.copied', 'Copied') : (copying ? t('downloads.copying', 'Copying...') : t('downloads.copy_link', 'Copy Link'))}</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled
              className="mt-3 inline-flex min-h-9 items-center gap-2 rounded-xl bg-gray-100 px-3 text-xs font-black text-gray-400 dark:bg-gray-700 dark:text-gray-500"
            >
              <Download className="w-4 h-4" />
              <span>{t('common.not_available', 'Not Available')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const resolveDownloadIcon = (name = '', index = 0) => {
  const normalized = String(name).toLowerCase();
  if (normalized.includes('psh gg') || normalized.includes('app')) return Box;
  if (normalized.includes('menu')) return TerminalSquare;
  if (normalized.includes('script')) return FileCode;
  return index % 2 === 0 ? Box : FileCode;
};

const heroShellClass = 'relative mb-4 overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 p-4 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl sm:p-5';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroIconPanelClass = 'border border-primary-100 bg-white/70 text-primary-700 shadow-sm ring-1 ring-primary-100 dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:ring-white/10 dark:shadow-none';

const Downloads = () => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to={getAuthRedirectPath()} replace />;
  }

  const dynamicCards = Array.isArray(settings?.downloadItems)
    ? settings.downloadItems
      .filter((item) => item && typeof item === 'object' && item.link)
      .map((item, index) => ({
        title: item.name || t('downloads.item_fallback_title', { index: index + 1 }, `Download ${index + 1}`),
        description: item.description || t('downloads.item_fallback_description', 'Direct download link from admin settings.'),
        link: item.link,
        type: typeof item.type === 'string' ? item.type.trim().toLowerCase() : '',
        icon: resolveDownloadIcon(item.name, index),
      }))
    : [];
  const cards = [...dynamicCards].sort((a, b) => {
    const score = (type) => (type === 'lua' || type === 'json' ? 0 : 1);
    return score(a.type) - score(b.type);
  });

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-4">
        <div className={heroShellClass}>
          <div className={heroGlowClass} />
          <div className="relative flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className={`mb-2 ${heroChipClass}`}>
                <Sparkles className="h-3 w-3" />
                {cards.length} {cards.length === 1 ? 'file' : 'files'}
              </div>
              <h1 className="text-2xl font-black leading-tight sm:text-3xl">
                {t('downloads.title', 'Downloads')}
              </h1>
              <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                {t('downloads.subtitle', 'Download links managed from Admin Panel > Settings.')}
              </p>
            </div>
            <div className={`hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl sm:flex ${heroIconPanelClass}`}>
              <Download className="h-7 w-7" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {cards.length === 0 ? (
            <div className="lg:col-span-2 rounded-3xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm font-bold text-gray-500 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
              {t('downloads.empty', 'No downloads configured yet. Please add entries from Admin Panel > Settings.')}
            </div>
          ) : (
            cards.map((card, index) => (
              <DownloadCard
                key={`${card.title}-${index}`}
                icon={card.icon}
                title={card.title}
                description={card.description}
                link={card.link}
                type={card.type}
                t={t}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Downloads;
